import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { takeUntil, first } from 'rxjs/operators';
import { Threshold } from './threshold';
import { ApiService } from '../api/api.service';
import { Thresholds } from './thresholds';
import { HighchartsSeverity } from '../../services/highcharts/types';

export enum ThresholdSeverity {
  CRITICAL = 'critical',
  WARNING = 'warning',
  NORMAL = 'normal',
  GOOD = 'good'
}

@Injectable({
  providedIn: 'root'
})
export class ThresholdsService {
  private GetAllThresholdURL = '/anomaly/v2/thresholds/static/';
  private readonly unsubscribe: Subject<void> = new Subject();
  cache = new BehaviorSubject<Array<Threshold>>([]);
  realtimemonitorcache: Array<Threshold> = [];
  thresholds: Thresholds;

  constructor(private readonly apiService: ApiService) {}

  getEntityName(): string {
    return 'threshold';
  }

  getValues(): any[] {
    return this.getThresholds();
  }

  getTresholdsAsync(): Observable<Thresholds> {
    if (!this.thresholds) {
      return this.updateThresholds(this.chooseCacheModel()).pipe(first());
    }
    return of(this.thresholds).pipe(first());
  }

  getThresholds$(): Observable<any> {
    return this.apiService.get$<any>(this.GetAllThresholdURL, {});
  }

  //eslint-disable-next-line
  createThreshold$(data: any): Observable<any> {
    //eslint-disable-next-line
    return this.apiService.post$<any>(this.GetAllThresholdURL, data);
  }

  //eslint-disable-next-line
  updateThreshold$(data: any): Observable<any> {
    //eslint-disable-next-line
    return this.apiService.put$<any>(this.GetAllThresholdURL, data);
  }

  chooseCacheModel() {
    // simplified implementation/no support for history mode
    return this.realtimemonitorcache;
  }

  getThresholds() {
    return this.chooseCacheModel();
  }

  updateThresholds(cache: Array<Threshold>) {
    const updated = new Subject<Thresholds>();
    this.getThresholds$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.assignStaticThresholds(response);
          this.assignThresholdToCacheList(cache, response);
          updated.next(this.thresholds);
        },
        error => {
          this.assignStaticThresholds([]);
          this.assignThresholdToCacheList(cache, []);
          updated.complete();
        }
      );
    return updated;
  }

  assignStaticThresholds(response) {
    this.thresholds = new Thresholds(response);
  }

  assignThresholdToCacheList(cache: Array<Threshold>, data: Array<Threshold>): void {
    // simplified algo, that simply replaces full cache with new list
    // notice we preserve the array object!
    cache.length = 0;
    for (const entry of data) {
      cache.push(entry);
    }

    this.cache.next(this.chooseCacheModel());
  }

  dispose() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  validateStaticThresholdStatus(kpi: string, value: number, dimensionObj) {
    if (!this.thresholds) {
      // falsy value. Thresholds object may not yet exist - eg, immediately after login to the app, when delta service is lagging
      // to obtain data from backend.
      return null;
    }
    return this.thresholds.validateStaticThresholdStatus(kpi, value, dimensionObj);
  }

  getThresholdColorValue(
    thresholdSeverity: string,
    defaultColor: HighchartsSeverity
  ): HighchartsSeverity {
    if (thresholdSeverity === ThresholdSeverity.CRITICAL) {
      return HighchartsSeverity.CRITICAL;
    }
    if (thresholdSeverity === ThresholdSeverity.WARNING) {
      return HighchartsSeverity.WARNING;
    }
    return defaultColor;
  }

  _getThresholdZones(
    criticalThreshold: number,
    warningThreshold: number,
    defaultColor: HighchartsSeverity,
    expression?: string
  ): { value?: number; color: string }[] {
    const zones = [];
    const criticalThresholdValue = this.getThresholdColorValue(
      ThresholdSeverity.CRITICAL,
      defaultColor
    );
    const warningThresholdValue = this.getThresholdColorValue(
      ThresholdSeverity.WARNING,
      defaultColor
    );
    if (criticalThreshold) {
      criticalThreshold = this.getThresoldValueByExpression(expression, criticalThreshold);
      zones.push({
        value: criticalThreshold,
        color: expression === '<' ? criticalThresholdValue : defaultColor
      });
    }
    if (warningThreshold) {
      warningThreshold = this.getThresoldValueByExpression(expression, warningThreshold);
      zones.push({
        value: warningThreshold,
        color: warningThresholdValue
      });
    }
    zones.push({
      color: expression === '<' ? defaultColor : criticalThresholdValue
    });
    return zones;
  }

  getThresoldValueByExpression(expression: string, thresholdValue: number): number {
    if (expression === '>') {
      return thresholdValue + 0.03;
    } else if (expression === '<') {
      return thresholdValue - 0.03;
    }
    return thresholdValue;
  }
}
